// Libraries
import React from 'react'
import { Modal } from '@mui/material'
// Components
import CustomModalError from './CustomModalError/CustomModalError'
import CustomModalAvailability from './CustomModalAvailability/CustomModalAvailability'
import CustomModalMassApprovalRetry from './MassApproval/CustomModalMassApprovalRetry/CustomModalMassApprovalRetry'
import CustomModalMassApprovalConfirm from './MassApproval/CustomModalMassApprovalConfirm/CustomModalMassApprovalConfirm'
import CustomModalManageSchedule from './CustomModalSchedules/CustomModalManageSchedule'
import CustomModalAssignSelected from './CustomModalSchedules/CustomModalAssignSelected'
import CustomModalAssignCompleted from './CustomModalSchedules/CustomModalAssignCompleted'
import CustomModalErrorMessage from './CustomModalSchedules/CustomModalErrorMessage'
import CustomModalInfoMessage from './CustomModalSchedules/CustomModalInfoMessage'
import CustomModalScheduleTasks from './CustomModalSchedules/CustomModalScheduleTasks'
import CustomModalSameChapaServices from './CustomModalSchedules/CustomModalSameChapaServices'
import CustomModalSuccessMessage from './CustomModalSchedules/CustomModalSuccessMessage'
import CustomModalVehicleWorkshop from './CustomModalSchedules/CustomModalVehicleWorkshop'
import CustomModalActivityDeclarations from './CustomModalSchedules/CustomModalActivityDeclarations'
import CustomModalSendSchedule from './CustomModalSchedules/CustomModalSendSchedule'
import CustomModalSubcontractDrivers from './CustomModalSchedules/CustomModalSubcontractDrivers'
import CustomModalDriversWithoutRest from './CustomModalSchedules/CustomModalDriversWithoutRest'
import CustomModalDriversVehiclesAvailability from './CustomModalSchedules/CustomModalDriversVehiclesAvailability'
import CustomModalRestAfterWork from './CustomModalSchedules/CustomModalRestAfterWork'
import CustomModalServiceItinerary from './CustomModalSchedules/CustomModalServiceItinerary'
import CustomModalServiceDetail from './CustomModalSchedules/CustomModalServiceDetail'
import CustomModalTachographServiceTypes from './CustomModalSchedules/CustomModalTachographServiceTypes'
import CustomModalChapasPorMotoristas from './CustomModalSchedules/CustomModalChapasPorMotoristas'
import CustomModalVehicleServiceTotals from './CustomModalSchedules/CustomModalVehicleServiceTotals'
import CustomModalDriverServiceTotals from './CustomModalSchedules/CustomModalDriverServiceTotals'
import CustomModalDriverSituations from './CustomModalSchedules/CustomModalDriverSituations'
import CustomModalVehicleSituations from './CustomModalSchedules/CustomModalVehicleSituations'
import CustomModalVehicleAccidents from './CustomModalSchedules/CustomModalVehicleAccidents'
import CustomModalVehicleWash from './CustomModalSchedules/CustomModalVehicleWash'
import CustomModalNonScheduledDrivers from './CustomModalSchedules/CustomModalNonScheduledDrivers'
import CustomModalServicesByClient from './CustomModalSchedules/CustomModalServicesByClient'
import CustomModalServicesByLocality from './CustomModalSchedules/CustomModalServicesByLocality'
import CustomModalOvertimeHours from './CustomModalSchedules/CustomModalOvertimeHours'
import CustomModalTimesheetNotices from './CustomModalSchedules/CustomModalTimesheetNotices'
import CustomModalItinerarySheets from './CustomModalSchedules/CustomModalItinerarySheets'
import CustomModalTimesheetMultipleDays from './CustomModalSchedules/CustomModalTimesheetMultipleDays'
import CustomModalScheduleEmails from './CustomModalSchedules/CustomModalScheduleEmails'
// Types and interfaces
// import { FailedFeedback } from '../../../../types/massApproval'
import { MassApprovalRequest } from '../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import {
	modalTitleType,
	modalDataType,
	modalHandlersType,
	ErrorModalData,
	AvailabilityModalData,
	ScheduleModalData,
	ScheduleTasksModalData,
	ActivityDeclarationsModalData,
	AssignSelectedModalData,
	SameChapaModalData,
	SendScheduleModalData,
	SubcontractDriversModalData,
	ServiceItineraryModalData,
	ServiceDetailModalData,
	VehicleWorkshopModalData,
	DriversWithoutRestModalData,
	DriversVehiclesAvailabilityModalData,
	RestAfterWorkModalData,
	TachographServiceTypeModalData,
	ChapasPorMotoristasModalData,
	VehicleServiceTotalsModalData,
	DriverServiceTotalsModalData,
	VehicleAccidentsModalData,
	DriverSituationsModalData,
	VehicleSituationsModalData,
	VehicleWashModalData,
	StatusMessageModalData,
	NonScheduledDriversModalData,
	ServicesByClientModalData,
	ServicesByLocalityModalData,
	OvertimeModalData,
	TimesheetNoticesModalData,
	TimesheetMultipleDaysModalData,
	ScheduleEmailsModalData,
	ItinerarySheetsModalData,
} from '../../../../types/modal'
// Utils
// import { devLog } from '../../../../utils/feedback'
import { Provider } from 'react-redux'
import { useModal } from '../../../../store/ModalProvider'
import { timesheetStore } from '../../../../store/timesheetStore'
import { useCustomModalValidation } from './customModalValidation'
import CustomModalVirtualTimesheet from './CustomModalVirtualTimesheet/CustomModalVirtualTimesheet'

interface CustomModalProps {
	title: modalTitleType
	data: modalDataType
	handlers?: modalHandlersType
}

/**
 * ## Custom modal component
 *
 * - @param title - The title of the modal
 * - @param data - The data and handlers for the modal
 * - @returns The rendered modal component
 */
const CustomModal: React.FC<CustomModalProps> = ({ title, data }) => {
	const { isOpen, closeModal } = useModal()
	const titleText = String(title)
	const isSchedulesModal =
		titleText !== 'Disponibilidade e Pausas' &&
		titleText !== 'Error Details' &&
		!titleText.startsWith('Rever Folhas de Ponto Submetidas sem') &&
		!titleText.startsWith('Aprovar Folhas de Ponto sem') &&
		titleText !== 'Folha de Ponto Virtual'
	const validErrorModalData = useCustomModalValidation(title, data as unknown as ErrorModalData)
	const validAvailabilityModalData = useCustomModalValidation(title, data as unknown as AvailabilityModalData)
	// const validMassApprovalModalData = useCustomModalValidation(title, data as unknown as FailedFeedback)
	const validMassApprovalConfirmationModalData = useCustomModalValidation(
		title,
		data as unknown as MassApprovalRequest[],
	)
	const validScheduleModalData = useCustomModalValidation(title, data as unknown as ScheduleModalData)
	const validScheduleTasksModalData = useCustomModalValidation(title, data as unknown as ScheduleTasksModalData)
	const validActivityDeclarationsModalData = useCustomModalValidation(
		title,
		data as unknown as ActivityDeclarationsModalData,
	)
	const validSendScheduleModalData = useCustomModalValidation(title, data as unknown as SendScheduleModalData)
	const validSubcontractDriversModalData = useCustomModalValidation(
		title,
		data as unknown as SubcontractDriversModalData,
	)
	const validServiceItineraryModalData = useCustomModalValidation(title, data as unknown as ServiceItineraryModalData)
	const validServiceDetailModalData = useCustomModalValidation(title, data as unknown as ServiceDetailModalData)
	const validAssignSelectedModalData = useCustomModalValidation(title, data as unknown as AssignSelectedModalData)
	const validSameChapaModalData = useCustomModalValidation(title, data as unknown as SameChapaModalData)
	const validVehicleWorkshopModalData = useCustomModalValidation(title, data as unknown as VehicleWorkshopModalData)
	const validDriversWithoutRestModalData = useCustomModalValidation(
		title,
		data as unknown as DriversWithoutRestModalData,
	)
	const validRestAfterWorkModalData = useCustomModalValidation(title, data as unknown as RestAfterWorkModalData)
	const validDriversVehiclesAvailabilityModalData = useCustomModalValidation(
		title,
		data as unknown as DriversVehiclesAvailabilityModalData,
	)
	const validTachographServiceTypeModalData = useCustomModalValidation(
		title,
		data as unknown as TachographServiceTypeModalData,
	)
	const validChapasPorMotoristasModalData = useCustomModalValidation(
		title,
		data as unknown as ChapasPorMotoristasModalData,
	)
	const validVehicleServiceTotalsModalData = useCustomModalValidation(
		title,
		data as unknown as VehicleServiceTotalsModalData,
	)
	const validDriverServiceTotalsModalData = useCustomModalValidation(
		title,
		data as unknown as DriverServiceTotalsModalData,
	)
	const validDriverSituationsModalData = useCustomModalValidation(title, data as unknown as DriverSituationsModalData)
	const validVehicleSituationsModalData = useCustomModalValidation(title, data as unknown as VehicleSituationsModalData)
	const validVehicleAccidentsModalData = useCustomModalValidation(title, data as unknown as VehicleAccidentsModalData)
	const validVehicleWashModalData = useCustomModalValidation(title, data as unknown as VehicleWashModalData)
	const validNonScheduledDriversModalData = useCustomModalValidation(
		title,
		data as unknown as NonScheduledDriversModalData,
	)
	const validServicesByClientModalData = useCustomModalValidation(title, data as unknown as ServicesByClientModalData)
	const validServicesByLocalityModalData = useCustomModalValidation(
		title,
		data as unknown as ServicesByLocalityModalData,
	)
	const validOvertimeModalData = useCustomModalValidation(title, data as unknown as OvertimeModalData)
	const validTimesheetNoticesModalData = useCustomModalValidation(title, data as unknown as TimesheetNoticesModalData)
	const validTimesheetMultipleDaysModalData = useCustomModalValidation(
		title,
		data as unknown as TimesheetMultipleDaysModalData,
	)
	const validScheduleEmailsModalData = useCustomModalValidation(title, data as unknown as ScheduleEmailsModalData)
	const validItinerarySheetsModalData = useCustomModalValidation(title, data as unknown as ItinerarySheetsModalData)
	const validStatusMessageModalData = useCustomModalValidation(title, data as unknown as StatusMessageModalData)

	// devLog('CustomModal Rendered with title:', title, 'and data:', data);

	if (!isOpen) return null

	// Render CustomModalError when title is "Error Details" and data has errorCode
	return (
		<Modal
			open={isOpen}
			onClose={closeModal}
			aria-labelledby={title.toLocaleLowerCase().replace(/\s+/g, '-')}
			aria-describedby={title.toLocaleLowerCase().replace(/\s+/g, '-') + '-modal-description'}
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				pointerEvents: 'none',
				...(isSchedulesModal && {
					'&, & *': {
						animation: 'none !important',
						transition: 'none !important',
						scrollBehavior: 'auto !important',
					},
				}),
			}}
		>
			<>
				{title === 'Disponibilidade e Pausas' && validAvailabilityModalData && (
					// CustomModal is mounted once at the app root (App.tsx), outside the timesheet page's own
					// <Provider store={timesheetStore}> (set up per-route in pages/v2/home.tsx). Without this
					// wrapper, CustomModalAvailability's useSelector reads from the app-root store instead —
					// which doesn't even register the timesheetPage reducer — so `state.timesheetPage` is always
					// undefined here, independent of whatever WorkRegimeAndAgent dispatches. Same pattern already
					// used below for CustomModalVirtualTimesheet.
					<Provider store={timesheetStore}>
						<CustomModalAvailability />
					</Provider>
				)}
				{title === 'Error Details' && validErrorModalData && <CustomModalError />}
				{title === 'Gerir Escala' && validScheduleModalData && <CustomModalManageSchedule />}
				{title === 'Tarefas da Escala' && validScheduleTasksModalData && <CustomModalScheduleTasks />}
				{title === 'Declaracoes de Atividade' && validActivityDeclarationsModalData && (
					<CustomModalActivityDeclarations />
				)}
				{title === 'Enviar Escala' && validSendScheduleModalData && <CustomModalSendSchedule />}
				{title === 'Subcontratar Motoristas' && validSubcontractDriversModalData && <CustomModalSubcontractDrivers />}
				{title === 'Requisicao do Servico' && validServiceItineraryModalData && <CustomModalServiceItinerary />}
				{title === 'Detalhe do Servico' && validServiceDetailModalData && <CustomModalServiceDetail />}
				{title === 'Atribuir Selecionados' && validAssignSelectedModalData && <CustomModalAssignSelected />}
				{title === 'Atribuicao Concluida' && validAssignSelectedModalData && <CustomModalAssignCompleted />}
				{title === 'Viatura em Oficina' && validVehicleWorkshopModalData && <CustomModalVehicleWorkshop />}
				{title === 'Motoristas sem folga ha mais de 6 dias' && validDriversWithoutRestModalData && (
					<CustomModalDriversWithoutRest />
				)}
				{title === 'Descansos ap\u00f3s Folga / Complementar Trabalhada' && validRestAfterWorkModalData && (
					<CustomModalRestAfterWork />
				)}
				{title === 'Disponibilidade de Motoristas e Viaturas' && validDriversVehiclesAvailabilityModalData && (
					<CustomModalDriversVehiclesAvailability />
				)}
				{title === 'Servi\u00e7os por Tipo de Tac\u00f3grafo' && validTachographServiceTypeModalData && (
					<CustomModalTachographServiceTypes />
				)}
				{title === 'Chapas por Motoristas' && validChapasPorMotoristasModalData && <CustomModalChapasPorMotoristas />}
				{title === 'Alterar Motoristas n\u00e3o Escalados' && validNonScheduledDriversModalData && (
					<CustomModalNonScheduledDrivers />
				)}
				{title === 'Servicos e Totais por Viatura' && validVehicleServiceTotalsModalData && (
					<CustomModalVehicleServiceTotals />
				)}
				{title === 'Servicos e Totais por Motorista' && validDriverServiceTotalsModalData && (
					<CustomModalDriverServiceTotals />
				)}
				{title === 'Situa\u00e7\u00f5es do Motorista' && validDriverSituationsModalData && (
					<CustomModalDriverSituations />
				)}
				{title === 'Situa\u00e7\u00f5es da Viatura' && validVehicleSituationsModalData && (
					<CustomModalVehicleSituations />
				)}
				{title === 'Sinistralidade da Viatura' && validVehicleAccidentsModalData && <CustomModalVehicleAccidents />}
				{title === 'Servicos por Cliente' && validServicesByClientModalData && <CustomModalServicesByClient />}
				{title === 'Servi\u00e7os por Localidade' && validServicesByLocalityModalData && (
					<CustomModalServicesByLocality />
				)}
				{title === 'Horas Extra' && validOvertimeModalData && <CustomModalOvertimeHours />}
				{title === 'Folhas de Ponto e Avisos de Folga Atrasada' && validTimesheetNoticesModalData && (
					<CustomModalTimesheetNotices />
				)}
				{title === 'Folhas de Ponto de v\u00e1rios dias' && validTimesheetMultipleDaysModalData && (
					<CustomModalTimesheetMultipleDays />
				)}
				{title === 'Emails que recebem a escala' && validScheduleEmailsModalData && <CustomModalScheduleEmails />}
				{title === 'Folhas de Itiner\u00e1rio' && validItinerarySheetsModalData && <CustomModalItinerarySheets />}
				{title === 'Lavagem de Viaturas' && validVehicleWashModalData && <CustomModalVehicleWash />}
				{title === 'Mensagem Informativa' && validStatusMessageModalData && <CustomModalInfoMessage />}
				{title === 'Escala Enviada' && validStatusMessageModalData && <CustomModalSuccessMessage />}
				{title === 'Mensagem de Erro' && validStatusMessageModalData && <CustomModalErrorMessage />}
				{title === 'Escalar outros servi\u00e7os da mesma chapa / requisi\u00e7\u00e3o' && validSameChapaModalData && (
					<CustomModalSameChapaServices />
				)}
				{/* TODO: Fix validMassApprovalModalData */}
				{title === 'Rever Folhas de Ponto Submetidas sem Altera\u00e7\u00f5es' && (
					/* validMassApprovalModalData &&  */ <CustomModalMassApprovalRetry />
				)}
				{title === 'Aprovar Folhas de Ponto sem altera\u00e7\u00f5es' && validMassApprovalConfirmationModalData && (
					<CustomModalMassApprovalConfirm />
				)}
				{/* TODO: Aply correct prop values to CustomModalVirtualTimesheet */}
				{title === 'Folha de Ponto Virtual' && (
					<Provider store={timesheetStore}>
						<CustomModalVirtualTimesheet />
					</Provider>
				)}
			</>
		</Modal>
	)
}

export default CustomModal
